import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { useAuth } from '../context/AuthContext';
import { portalPath } from '../domain/identity';
import {
  familyPortalEmailKey, familyPortalOpen, familyPortalRefusal, familyPortalTokenFromHash, familyPortalWord as w,
  passwordMin,
} from '../domain/familyPortal';
import { describeJoinLink, redeemJoinLink } from '../data/familyPortal';
import s from './PortalJoinPage.module.css';

// /portal/join (#770): where a Family Portal invitation's link lands.
//
// MMS's link opens a page to set the initial password (its template's
// "Password Setup Link"). Ours does that, and also handles what MMS's does not
// have to — an address that already has a login here, a teacher's or another
// studio's family's:
//
//   * No login has the address: choose a password (twice), and the server
//     makes the login, verified, with the portal claim, and hands back a token
//     this page signs in with. Then /portal.
//   * A login has the address: sign in with it, and the link binds this
//     studio's family to that login. "Forgot your password?" is Firebase's own
//     reset email, the one the teacher's sign-in page sends.
//   * Signed in already: as the invited address, one button accepts; as anyone
//     else, the page says whose invitation it is and offers Sign out.
//
// Returning visits use email and password on the Family Portal's own sign-in
// page, /portal/signin (#771, src/pages/PortalSignInPage.jsx), which offers no
// trial. There is no email-link sign-in (MMS has none).
//
// The token is in the URL's fragment (`#t=…`), which no server log and no
// Referer carries. The page asks the server what the link is before showing
// any form; a dead link — used, expired, sent again since, revoked — is one
// sentence, whichever it was.
//
// While the Family Portal is closed (#769) the page says so and asks nothing.

// Set once this page has signed a new login in, so the remount the identity
// gate causes as that login arrives goes straight on to /portal rather than
// asking the server about a link it has just used.
let finished = false;

export default function PortalJoinPage() {
  const {
    user, loading, login, logout, resetPassword, signInWithPortalToken, refreshVerification,
  } = useAuth();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const token = familyPortalTokenFromHash(hash);
  const open = familyPortalOpen();

  const [info, setInfo] = useState({ token: null, value: null, error: null });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [mustSignIn, setMustSignIn] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    if (!open || !token || finished) return undefined;
    let live = true;
    describeJoinLink(token).then(
      (value) => { if (live) setInfo({ token, value, error: null }); },
      (err) => { if (live) setInfo({ token, value: null, error: err.message }); },
    );
    return () => { live = false; };
  }, [open, token]);

  if (finished && user) return <Navigate to={portalPath()} replace />;

  const shell = (children) => (
    <main className={s.page}>
      <div className={s.card}>
        <h1 className={s.title}>{w('name')}</h1>
        {children}
      </div>
    </main>
  );

  if (!open) return shell(<p className={s.body}>{w('closedBody')}</p>);
  if (!token) return shell(<p className={s.body}>{w('joinMissing')}</p>);
  const mine = info.token === token ? info : { value: null, error: null };
  if (mine.error) return shell(<p className={s.body}>{mine.error}</p>);
  if (!mine.value || loading) return shell(<p className={s.body}>{w('joinChecking')}</p>);

  const { studioName, email, hasLogin } = mine.value;
  const invited = (
    <p className={s.body}>
      {w('joinInvitedTo')} <span className={s.studio}>{studioName || w('joinStudioFallback')}</span>.
    </p>
  );

  const fail = (err) => { setBusy(false); setError(err?.message || String(err)); };

  // Signed in: accept as this login, or say whose invitation it is. While a
  // sign-in below is accepting, it is only waiting.
  if (user && busy) return shell(<p className={s.body}>{w('joinChecking')}</p>);
  if (user) {
    const same = familyPortalEmailKey(user.email) === familyPortalEmailKey(email);
    const accept = () => {
      setBusy(true);
      setError(null);
      redeemJoinLink({ token }).then(async () => {
        finished = true;
        // The server may just have confirmed this login's address.
        await Promise.resolve(refreshVerification?.()).then(() => {}, () => {});
        navigate(portalPath(), { replace: true });
      }, fail);
    };
    return shell(
      <>
        {invited}
        <p className={s.body}>{w('joinSignedInAs')} {user.email}.</p>
        {same ? null : <p className={s.error}>{familyPortalRefusal('wrong_account')}</p>}
        {error ? <p className={s.error}>{error}</p> : null}
        <div className={s.form}>
          {same ? <Button onClick={accept} loading={busy}>{w('joinAccept')}</Button> : null}
          <button type="button" className={s.link} onClick={() => logout()}>{w('joinSignOut')}</button>
        </div>
      </>,
    );
  }

  // Signed out, and the address has a login: sign in, then accept.
  if (hasLogin || mustSignIn) {
    const signIn = (e) => {
      e.preventDefault();
      setBusy(true);
      setError(null);
      login(email, password).then(
        () => redeemJoinLink({ token }).then(() => {
          finished = true;
          navigate(portalPath(), { replace: true });
        }, fail),
        () => fail(new Error(familyPortalRefusal('wrong_password'))),
      );
    };
    const forgot = () => {
      setError(null);
      resetPassword(email).then(() => setResetSent(true), () => setResetSent(true));
    };
    return shell(
      <>
        {invited}
        <p className={s.body}>{w('joinHasLogin')}</p>
        <form className={s.form} onSubmit={signIn}>
          <label className={s.label}>
            {w('joinEmail')}
            <input className={[s.input, s.readonly].join(' ')} value={email} readOnly />
          </label>
          <label className={s.label}>
            {w('joinPassword')}
            <input
              className={s.input} type="password" autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {error ? <p className={s.error}>{error}</p> : null}
          {resetSent ? <p className={s.body}>{w('joinResetSent')}</p> : null}
          <Button type="submit" loading={busy} disabled={!password}>{w('joinSignIn')}</Button>
          <button type="button" className={s.link} onClick={forgot}>{w('joinForgot')}</button>
        </form>
      </>,
    );
  }

  // Signed out, and no login has the address: choose a password.
  const mismatch = confirm !== '' && confirm !== password;
  const create = (e) => {
    e.preventDefault();
    if (password.length < passwordMin()) { setError(familyPortalRefusal('password_short')); return; }
    if (password !== confirm) { setError(w('joinMismatch')); return; }
    setBusy(true);
    setError(null);
    redeemJoinLink({ token, password }).then(async (result) => {
      if (result?.signIn) {
        // A login was made for the address after this page asked: sign in.
        setBusy(false);
        setMustSignIn(true);
        return;
      }
      finished = true;
      await signInWithPortalToken(result.customToken);
      navigate(portalPath(), { replace: true });
    }, fail);
  };
  return shell(
    <>
      {invited}
      <p className={s.body}>{w('joinChoosePassword')}</p>
      <form className={s.form} onSubmit={create}>
        <label className={s.label}>
          {w('joinEmail')}
          <input className={[s.input, s.readonly].join(' ')} value={email} readOnly autoComplete="username" />
        </label>
        <label className={s.label}>
          {w('joinPassword')}
          <input
            className={s.input} type="password" autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label className={s.label}>
          {w('joinConfirmPassword')}
          <input
            className={s.input} type="password" autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
          />
        </label>
        {mismatch ? <p className={s.error}>{w('joinMismatch')}</p> : null}
        {error ? <p className={s.error}>{error}</p> : null}
        <Button type="submit" loading={busy} disabled={!password || !confirm}>{w('joinSetPassword')}</Button>
      </form>
    </>,
  );
}

/** Tests only: forget that this page signed a login in. */
export function resetJoinPageForTests() {
  finished = false;
}
